import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { adminService } from 'shared/api'
import { OrderModel } from 'shared/models'
import { pluralizationRu } from 'shared/utils/pluralization-ru'

function GiftCardReedemDialog({
  giftCardId,
  onClose,
  open
}: {
  giftCardId: number
  open: boolean
  onClose: () => void
}) {
  const [pin, setPin] = useState('')
  const [force, setForce] = useState(false)
  const [staffComment, setStaffComment] = useState('')
  const [order, setOrder] = useState<OrderModel | null>(null)
  const [processing, setProcessing] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const [orders, setOrders] = useState<OrderModel[]>([])

  useQuery(
    ['orderListAutocomplete', inputValue],
    ({ signal }) => adminService.order.getOrderList({ term: inputValue, page: 1, limit: 100 }, { signal }),
    {
      onSuccess: (data) => {
        setOrders(order ? [order, ...(data?.data?.list || [])] : data?.data?.list || [])
      },
      enabled: !!inputValue && (!order?.id || inputValue === order?.id)
    }
  )

  useEffect(() => {
    if (open === true) {
      setStaffComment('')
    }
  }, [open])

  function handleStaffCommentInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setStaffComment(e.target.value)
  }

  function handlePinInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setPin(e.target.value)
  }

  async function handleReedem() {
    setProcessing(true)

    try {
      await adminService.giftCard.reedem({ giftCardId, staffComment, orderId: order?.id || null, pin, force })
    } finally {
      setProcessing(false)
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Погашение сертификата</DialogTitle>
      <DialogContent>
        <Box
          marginTop="1rem"
          width="100%"
          display="flex"
          gap="0.5rem"
          alignItems="center"
          justifyContent="space-between"
        >
          <TextField
            label="Пин-код"
            placeholder="Пин-код"
            value={pin}
            variant="standard"
            InputLabelProps={{ shrink: true }}
            onChange={handlePinInputChange}
            disabled={force}
          />
          <FormControlLabel
            style={{ width: '228px', display: 'flex', justifyContent: 'space-between' }}
            control={<Switch checked={force} onChange={(e) => setForce(e.target.checked)} size="small" />}
            label="Без проверки пин-кода"
            labelPlacement="start"
          />
        </Box>
        <Box marginTop="1rem" width="100%">
          <TextField
            label="Комментарий сотрудника"
            placeholder="Комментарий сотрудника"
            value={staffComment}
            variant="standard"
            InputLabelProps={{ shrink: true }}
            onChange={handleStaffCommentInputChange}
            fullWidth
            multiline
            minRows={3}
          />
        </Box>
        <Box marginTop="1rem" width="100%">
          <Autocomplete
            getOptionLabel={(option: OrderModel) => option.id}
            filterOptions={(x) => x}
            options={orders}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={order}
            noOptionsText="Нет подходящих результатов"
            onChange={(_, newValue: OrderModel | null) => {
              setOrders((curr) => (newValue ? [newValue, ...curr] : curr))
              setOrder(newValue)
            }}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue)
            }}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Заказ"
                placeholder="Заказ"
                variant="standard"
                InputLabelProps={{ shrink: true }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props

              const serviceWithReservation = option.services.find((s) => !!s.reservation)

              return (
                <li key={key} {...optionProps}>
                  <Box>
                    <Box style={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                      <div>{option.customer?.name}</div>
                      <Box display="flex" gap="0.5rem" flexWrap="wrap" width="100%">
                        <Typography fontSize="11px">{option.customer?.phoneNumber}</Typography>
                        <Typography fontSize="11px">{option.customer?.email}</Typography>
                      </Box>
                    </Box>
                    <Box marginTop="0.5rem" display="flex" flexDirection="column">
                      {option.services
                        .sort((a, b) => Number(!!b.service.withReservation) - Number(!!a.service.withReservation))
                        .map((s) => (
                          <div
                            key={s.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              opacity: s?.service?.withReservation ? 1 : 0.64,
                              fontSize: s?.service?.withReservation ? '14px' : '12px'
                            }}
                          >
                            <div
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '250px',
                                flex: 1
                              }}
                            >
                              {s?.service?.withReservation ? '' : '+ '}
                              {s.service.name}
                            </div>
                            <div style={{ fontSize: '12px', opacity: 0.86 }}>{s?.price} ₽</div>
                          </div>
                        ))}
                    </Box>
                    {!!serviceWithReservation?.reservation && (
                      <Box marginTop="0.5rem">
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                          {Intl.DateTimeFormat('ru', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                          }).format(serviceWithReservation.reservation.startTime)}
                        </div>
                        <div style={{ fontSize: '12px' }}>
                          {[
                            Intl.DateTimeFormat('ru', { hour: '2-digit', minute: '2-digit' }).format(
                              serviceWithReservation.reservation.startTime
                            ),
                            Intl.DateTimeFormat('ru', { hour: '2-digit', minute: '2-digit' }).format(
                              serviceWithReservation.reservation.endTime
                            )
                          ].join(' - ')}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.56 }}>
                          {serviceWithReservation.reservation.persons}{' '}
                          {pluralizationRu(serviceWithReservation.reservation.persons || 1, [
                            'человек',
                            'человека',
                            'человек'
                          ])}
                        </div>
                      </Box>
                    )}
                  </Box>
                </li>
              )
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="submit" variant="outlined" disabled={processing || (!pin && !force)} onClick={handleReedem}>
          Погасить
        </Button>
      </DialogActions>
    </Dialog>
  )
}
export { GiftCardReedemDialog }
